import {
  ArrowLeft,
  ArrowRight,
  Compass,
  Grid3x3,
  Leaf,
  Map,
  MessageCircle,
  Satellite,
  Sprout,
  TreePine,
  Wheat,
  X,
  Building2,
  GitCompareArrows,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { WIZARD_STEPS } from "../config/agricultural-lulc";
import type {
  WizardStepId,
  DemoYear,
  CropVariant,
} from "../types/agricultural-lulc";
import { YearToggle } from "./year-toggle";

const STEP_ICONS: Record<WizardStepId, React.ComponentType<{ className?: string }>> = {
  challenge: Map,
  satellite: Satellite,
  lulc: Leaf,
  crops: Wheat,
  forest: TreePine,
  parcel: Building2,
  comparison: GitCompareArrows,
  explore: Compass,
};

interface WizardCardProps {
  currentStep: number;
  year: DemoYear;
  showPatches: boolean;
  cropVariant: CropVariant;
  onYearChange: (year: DemoYear) => void;
  onTogglePatches: () => void;
  onCropVariantChange: (variant: CropVariant) => void;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  onSpeakToExpert: () => void;
}

export function WizardCard({
  currentStep,
  year,
  showPatches,
  cropVariant,
  onYearChange,
  onTogglePatches,
  onCropVariantChange,
  onNext,
  onBack,
  onClose,
  onSpeakToExpert,
}: WizardCardProps) {
  const step = WIZARD_STEPS[currentStep];
  const StepIcon = STEP_ICONS[step.id];
  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <Card className="border-border bg-card/95 backdrop-blur supports-backdrop-filter:bg-card/90 w-105">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            Step {currentStep + 1} of {WIZARD_STEPS.length}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex size-8 shrink-0 items-center justify-center rounded bg-primary/10">
            <StepIcon className="size-4 text-primary" />
          </div>
          <CardTitle className="text-lg">{step.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pb-4">
        <CardDescription className="text-sm leading-relaxed">
          {step.description}
        </CardDescription>
        {step.id === "crops" && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Layer:</span>
            <div className="flex items-center gap-1 rounded border border-border p-0.5">
              <Button
                variant={cropVariant === "crops" ? "default" : "ghost"}
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => onCropVariantChange("crops")}
                title="Crop pixel raster summer 2024 (Sorghum / Cotton / Other / Fallow)"
              >
                Crops · S2024
              </Button>
              <Button
                variant={cropVariant === "harvest" ? "default" : "ghost"}
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => onCropVariantChange("harvest")}
                title="Harvest pixel raster summer 2024 (harvested vs standing per crop)"
              >
                Harvest · S2024
              </Button>
            </div>
          </div>
        )}
        {step.showYearToggle && (
          <div className="mt-3 flex items-center gap-2">
            <YearToggle year={year} onChange={onYearChange} excludeChange={step.id === "satellite"} />
            {step.id !== "lulc" && year === "change" && (
              <Button
                variant={showPatches ? "default" : "outline"}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={onTogglePatches}
                title={showPatches ? "Hide area patches" : "Show area patches"}
              >
                <Grid3x3 className="size-3.5" />
              </Button>
            )}
          </div>
        )}
        {/* Step progress dots */}
        <div className="flex items-center gap-1.5 mt-4">
          {WIZARD_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === currentStep
                  ? "w-6 bg-primary"
                  : i < currentStep
                    ? "w-1.5 bg-primary/50"
                    : "w-1.5 bg-muted-foreground/30"
              }`}
            />
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex items-center justify-between pt-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          disabled={isFirstStep}
        >
          <ArrowLeft className="size-4 mr-1" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          {isLastStep && (
            <Button
              size="sm"
              variant="outline"
              onClick={onSpeakToExpert}
            >
              <MessageCircle className="size-4 mr-1" />
              Speak to an Expert
            </Button>
          )}
          <Button size="sm" onClick={onNext}>
            {isLastStep ? (
              <>
                Start Exploring
                <Sprout className="size-4 ml-1" />
              </>
            ) : (
              <>
                Next
                <ArrowRight className="size-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
